// ---------------------------------------------------------------------------
// TzSchedulingForm (OPUS-D13)
// ---------------------------------------------------------------------------
// Upsert form for a per-(campaign, affiliate) IANA timezone rule.
//
// Intentionally self-contained — the form manages its own local state and
// validation so that the route component can stay declarative and drive a
// single mutation through TanStack Query. Zod4 + react-hook-form are not in
// the scaffold dep-lock, so we hand-roll an equivalent `safeParse` flow
// (see `tzFormSchema` in ./api.ts).
// ---------------------------------------------------------------------------

import { useId, useMemo, useState } from "react";
import {
	CAMPAIGN_DEFAULT_AFFILIATE,
	CURATED_TIMEZONES,
	normalizeTimezoneClient,
	tzFormSchema,
	type TzFormInput,
	type TzFormParsed,
} from "./api";

// ---------------------------------------------------------------------------
// Input primitives — kept inline to keep the owned-file set tight.
// ---------------------------------------------------------------------------

const inputClass =
	"w-full rounded-[6px] border bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500/50";

const inputErrorClass = "border-red-400 focus:ring-red-400/30 focus:border-red-500";

const labelClass = "mb-1 block text-xs font-medium text-zinc-600";

const helpClass = "mt-1 text-xs text-zinc-500";

const errorClass = "mt-1 text-xs text-red-600";

const buttonPrimary =
	"inline-flex items-center justify-center rounded-[6px] bg-gradient-to-r from-emerald-500 to-sky-500 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:from-emerald-600 hover:to-sky-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 disabled:cursor-not-allowed disabled:opacity-60";

const buttonSecondary =
	"inline-flex items-center justify-center rounded-[6px] border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 transition hover:border-zinc-300 hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-zinc-400/30";

// ---------------------------------------------------------------------------
// Props — consumer passes initial values + a submit callback that performs
// the actual mutation. The form owns validation + submit lifecycle.
// ---------------------------------------------------------------------------

export interface TzSchedulingFormProps {
	initialCampaignId?: string;
	initialAffiliateId?: string;
	initialTz?: string;
	isSubmitting?: boolean;
	onSubmit: (parsed: TzFormParsed) => void | Promise<void>;
	onCampaignChange?: (campaignId: string) => void;
}

interface FormState extends TzFormInput {
	// nothing extra — alias for clarity
}

export function TzSchedulingForm(props: TzSchedulingFormProps) {
	const [form, setForm] = useState<FormState>({
		campaignId: props.initialCampaignId ?? "",
		affiliateId: props.initialAffiliateId ?? "",
		tz: props.initialTz ?? guessBrowserTz(),
	});
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const campaignId = useId();
	const affiliateId = useId();
	const tzId = useId();
	const tzDatalistId = useId();

	const tzHint = useMemo(() => {
		if (!form.tz.trim()) return null;
		const normalized = normalizeTimezoneClient(form.tz);
		if (!normalized) return null;
		try {
			const now = new Date();
			const fmt = new Intl.DateTimeFormat("en-US", {
				timeZone: normalized,
				hour: "2-digit",
				minute: "2-digit",
				hour12: false,
			});
			return `Local time: ${fmt.format(now)}`;
		} catch {
			return null;
		}
	}, [form.tz]);

	function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
		setForm((prev) => ({ ...prev, [key]: value }));
		if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
		if (key === "campaignId" && props.onCampaignChange) {
			props.onCampaignChange(String(value));
		}
	}

	function markTouched(key: keyof FormState) {
		setTouched((prev) => ({ ...prev, [key]: true }));
	}

	function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
		event.preventDefault();
		setTouched({ campaignId: true, affiliateId: true, tz: true });
		const result = tzFormSchema.safeParse(form);
		if (!result.success) {
			setErrors(result.errors);
			return;
		}
		setErrors({});
		// Fire and forget — the parent handles mutation lifecycle.
		void props.onSubmit(result.data);
	}

	function showError(key: keyof FormState): string | null {
		if (!touched[key]) return null;
		const msg = errors[key];
		return msg && msg.length > 0 ? msg : null;
	}

	return (
		<form
			onSubmit={handleSubmit}
			className="rounded-[8px] border border-zinc-200 bg-white p-6 shadow-sm"
			aria-label="Timezone scheduling form"
		>
			<div className="grid gap-4 sm:grid-cols-2">
				<div>
					<label htmlFor={campaignId} className={labelClass}>
						Campaign ID
					</label>
					<input
						id={campaignId}
						type="text"
						value={form.campaignId}
						onChange={(e) => updateField("campaignId", e.target.value)}
						onBlur={() => markTouched("campaignId")}
						placeholder="camp_abc123"
						autoComplete="off"
						aria-invalid={showError("campaignId") !== null}
						aria-describedby={showError("campaignId") ? `${campaignId}-err` : undefined}
						className={`${inputClass} ${
							showError("campaignId") ? inputErrorClass : "border-zinc-200"
						}`}
					/>
					{showError("campaignId") ? (
						<p id={`${campaignId}-err`} className={errorClass}>
							{showError("campaignId")}
						</p>
					) : (
						<p className={helpClass}>Scope the rule to a single campaign.</p>
					)}
				</div>

				<div>
					<label htmlFor={affiliateId} className={labelClass}>
						Affiliate ID
					</label>
					<input
						id={affiliateId}
						type="text"
						value={form.affiliateId}
						onChange={(e) => updateField("affiliateId", e.target.value)}
						onBlur={() => markTouched("affiliateId")}
						placeholder={`${CAMPAIGN_DEFAULT_AFFILIATE} (campaign default)`}
						autoComplete="off"
						aria-invalid={showError("affiliateId") !== null}
						aria-describedby={
							showError("affiliateId") ? `${affiliateId}-err` : undefined
						}
						className={`${inputClass} ${
							showError("affiliateId") ? inputErrorClass : "border-zinc-200"
						}`}
					/>
					{showError("affiliateId") ? (
						<p id={`${affiliateId}-err`} className={errorClass}>
							{showError("affiliateId")}
						</p>
					) : (
						<p className={helpClass}>
							Leave blank or use <code className="text-zinc-700">*</code> for the
							campaign default.
						</p>
					)}
				</div>

				<div className="sm:col-span-2">
					<label htmlFor={tzId} className={labelClass}>
						IANA timezone
					</label>
					<input
						id={tzId}
						type="text"
						value={form.tz}
						onChange={(e) => updateField("tz", e.target.value)}
						onBlur={() => markTouched("tz")}
						list={tzDatalistId}
						placeholder="America/New_York"
						autoComplete="off"
						spellCheck={false}
						aria-invalid={showError("tz") !== null}
						aria-describedby={showError("tz") ? `${tzId}-err` : undefined}
						className={`${inputClass} ${
							showError("tz") ? inputErrorClass : "border-zinc-200"
						}`}
					/>
					<datalist id={tzDatalistId}>
						{CURATED_TIMEZONES.map((zone) => (
							<option key={zone} value={zone} />
						))}
					</datalist>
					{showError("tz") ? (
						<p id={`${tzId}-err`} className={errorClass}>
							{showError("tz")}
						</p>
					) : (
						<p className={helpClass}>
							{tzHint ?? "Start typing to filter zones (e.g. Europe/Berlin)."}
						</p>
					)}
				</div>
			</div>

			<div className="mt-6 flex items-center justify-end gap-3">
				<button
					type="button"
					className={buttonSecondary}
					onClick={() => {
						setForm({
							campaignId: props.initialCampaignId ?? "",
							affiliateId: props.initialAffiliateId ?? "",
							tz: props.initialTz ?? guessBrowserTz(),
						});
						setErrors({});
						setTouched({});
					}}
				>
					Reset
				</button>
				<button
					type="submit"
					className={buttonPrimary}
					disabled={props.isSubmitting === true}
				>
					{props.isSubmitting ? "Saving…" : "Save rule"}
				</button>
			</div>
		</form>
	);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function guessBrowserTz(): string {
	try {
		const guess = Intl.DateTimeFormat().resolvedOptions().timeZone;
		if (guess && guess.length > 0) return guess;
	} catch {
		// Fall through to UTC.
	}
	return "UTC";
}

export default TzSchedulingForm;
